import React, { FC, useMemo } from "react";
import { View } from "react-native";
import { useI18nNumber } from "@lookiero/i18n-react";
import { Currency } from "@lookiero/sty-psp-locale";
import { Text, TextLevel, TextVariant } from "@lookiero/sty-psp-ui";
import { DOMAIN } from "../../../i18n/i18n";
import { style as priceStyle } from "./Price.style";

type PriceVariant = "default" | "detail" | "subtotal" | "total";

interface PriceVariantProps {
  readonly variant?: TextVariant;
  readonly level?: TextLevel;
}

const PRICE_VARIANT: Partial<Record<PriceVariant, PriceVariantProps>> = {
  default: { variant: "detail", level: 1 },
  detail: { variant: "detail", level: 2 },
  subtotal: { variant: "detail", level: 1 },
  total: { variant: "action", level: 2 },
};

interface PriceProps {
  readonly amount: number;
  readonly currency: Currency;
  readonly discountedPrice?: {
    readonly amount: number;
    readonly percentage: number;
  };
  readonly variant?: PriceVariant;
  readonly withPercentage?: boolean;
}
const Price: FC<PriceProps> = ({ amount, currency, discountedPrice, variant = "default", withPercentage = false }) => {
  const isDiscounted = discountedPrice && discountedPrice.percentage > 0;
  const productPrice = useI18nNumber({
    domain: DOMAIN,
    value: amount / 100,
    style: "currency",
    currency,
  });
  const productDiscountedPrice = useI18nNumber({
    domain: DOMAIN,
    value: (discountedPrice?.amount || 0) / 100,
    style: "currency",
    currency,
  });

  const priceValue = isDiscounted ? productDiscountedPrice : productPrice;

  const style = useMemo(() => priceStyle(), []);

  return (
    <View style={style.price} testID="price">
      <Text {...PRICE_VARIANT[variant]} testID="price-text">
        {priceValue}
      </Text>
      {isDiscounted && (
        <View style={style.discountedPriceInfo}>
          <Text {...PRICE_VARIANT[variant]} style={style.discountedPriceText} testID="discounted-price-text">
            {productPrice}
          </Text>
          {withPercentage ? (
            <Text {...PRICE_VARIANT[variant]} style={style.discountedPercentage} testID="discounted-percentage-text">
              {`-${discountedPrice.percentage}%`}
            </Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

export { Price };
